import { useEffect, useState, startTransition, useRef } from 'react';

export default function TapSurface({
  children,
  onTap,
  isActive = false,
  activeColor = '#2563eb',
  inactiveColor = '#1a1a1a',
  activeTextColor = '#ffffff',
  inactiveTextColor = '#3b82f6',
  className = '',
  style = {},
  disabled = false,
  // Opt-in: treat this surface as a TOGGLE and flip its highlight
  // optimistically on tap, before the parent re-render confirms it via
  // `isActive`. Only enable on true toggles (the odds buttons). Leave OFF
  // for action-style buttons (Cash Out, Start Demo) whose `isActive` is a
  // fixed value — flipping those optimistically would invert their palette.
  optimisticToggle = false,
  // Opt-in: fire the handler on pointerDown (touchstart) instead of click.
  // Eliminates the iOS ~50-100ms click delay so the highlight is instant.
  // ONLY set this on reversible toggles (odds buttons). NEVER set it on
  // money-flow actions (Place Pik, Cash Out, withdrawals) where an
  // accidental scroll-start on the button must not trigger a transaction.
  instantTouch = false,
  // Spread any additional props (aria-*, title, data-*, etc.) onto the
  // underlying <button> so callers can attach accessibility metadata
  // and tooltips without needing a custom wrapper.
  ...rest
}) {
  // Optimistic highlight: flip the visual state the instant the tap lands,
  // BEFORE the (potentially huge) parent re-render that makes `isActive`
  // catch up. On the games board a bet-slip change re-renders hundreds of
  // cards — without this the highlight lags the tap by whatever that render
  // costs on a phone. `pending` is the optimistic value; it clears as soon
  // as the prop confirms it, or reverts after a timeout if the parent never
  // reacts (e.g. an action-style TapSurface that isn't a toggle).
  const [pending, setPending] = useState(null);
  const shownActive = pending ?? isActive;

  useEffect(() => {
    if (pending === null) return undefined;
    if (isActive === pending) {
      setPending(null);
      return undefined;
    }
    // Safety net only: a toggle's `isActive` normally confirms well before
    // this. Long enough that a slow transition commit on a weak phone never
    // blinks the highlight off mid-flight.
    const t = setTimeout(() => setPending(null), 2000);
    return () => clearTimeout(t);
  }, [isActive, pending]);

  // Guard against double-firing: when instantTouch fires on pointerDown,
  // the browser still dispatches a synthetic click event shortly after.
  // This ref lets the onClick handler recognise and swallow that echo.
  const touchFiredRef = useRef(false);

  const handleFire = () => {
    if (disabled || !onTap) return;
    if (optimisticToggle) {
      setPending(!shownActive);
      startTransition(() => onTap());
    } else {
      onTap();
    }
  };

  return (
    <button
      {...rest}
      type="button"
      disabled={disabled}
      // Touch: fire immediately on finger-down (zero click delay).
      // Only for instantTouch surfaces and only for actual touch input —
      // mouse buttons keep normal click semantics.
      onPointerDown={instantTouch ? (e) => {
        if (e.pointerType !== 'touch' || disabled || !onTap) return;
        touchFiredRef.current = true;
        handleFire();
      } : undefined}
      onClick={() => {
        // Swallow the synthetic click that follows a touch pointerDown when
        // instantTouch already handled it.
        if (touchFiredRef.current) { touchFiredRef.current = false; return; }
        handleFire();
      }}
      style={{
        // Use CSS-var fallbacks for the inactive (default) palette so
        // light mode can recolor every TapSurface globally via
        // `--tap-inactive-bg` / `--tap-inactive-text` without losing
        // the caller-provided props (the prop value is the fallback).
        // Active state is left as the raw prop so caller-driven
        // selection highlights (e.g. blue when a bet is in the slip)
        // always win.
        backgroundColor: shownActive ? activeColor : `var(--tap-inactive-bg, ${inactiveColor})`,
        color: shownActive ? activeTextColor : `var(--tap-inactive-text, ${inactiveTextColor})`,
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        touchAction: 'manipulation',
        outline: 'none',
        border: 'none',
        boxShadow: 'none',
        transition: 'none',
        ...style,
      }}
      className={`no-hover-effect ${className}`.trim()}
    >
      {children}
    </button>
  );
}
